import { createClient } from '@supabase/supabase-js';

// No Vite, usamos import.meta.env e não process.env
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Erro: Variáveis do Supabase não encontradas no arquivo .env');
  throw new Error('Missing Supabase environment variables');
}

/**
 * Usa sessionStorage em vez de localStorage para permitir múltiplas sessões
 * simultâneas em abas diferentes (ex.: Admin em uma aba, Psicólogo em outra).
 * Cada aba mantém sua própria sessão independente.
 */
const supabaseOptions =
  typeof window !== 'undefined'
    ? {
        auth: {
          storage: window.sessionStorage,
          storageKey: 'essencia-auth',
        },
      }
    : {};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, supabaseOptions);

export type Database = {
  public: {
    Tables: {
      users: {
        Row: {
          id: string;
          email: string;
          full_name: string;
          user_type: 'patient' | 'psychologist';
          phone: string | null;
          cpf: string | null;
          photo_url: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['users']['Row'], 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['users']['Insert']>;
      };
      psychologist_profiles: {
        Row: {
          id: string;
          user_id: string;
          crp: string;
          bio: string | null;
          specialties: string[];
          approaches: string[];
          languages: string[];
          session_price: number;
          experience_years: number | null;
          education: string | null;
          is_verified: boolean;
          is_active: boolean;
          rating: number;
          total_reviews: number;
          total_sessions: number;
          pix_key: string | null;
          pix_type: string | null;
          created_at: string;
          updated_at: string;
        };
      };
      appointments: {
        Row: {
          id: string;
          patient_id: string;
          psychologist_id: string;
          scheduled_at: string;
          duration_minutes: number;
          status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
          meeting_link: string | null;
          notes: string | null;
          created_at: string;
          updated_at: string;
        };
      };
      transactions: {
        Row: {
          id: string;
          appointment_id: string;
          patient_id: string;
          psychologist_id: string;
          amount: number;
          status: 'pending' | 'completed' | 'refunded' | 'failed';
          payment_method: string | null;
          stripe_payment_id: string | null;
          created_at: string;
          updated_at: string;
        };
      };
      medical_records: {
        Row: {
          id: string;
          patient_id: string;
          psychologist_id: string;
          appointment_id: string | null;
          diagnosis: string | null;
          treatment_plan: string | null;
          notes: string;
          attachments: string[];
          created_at: string;
          updated_at: string;
        };
      };
      reviews: {
        Row: {
          id: string;
          appointment_id: string;
          patient_id: string;
          psychologist_id: string;
          rating: number;
          comment: string | null;
          is_published: boolean;
          created_at: string;
          updated_at: string;
        };
      };
      notifications: {
        Row: {
          id: string;
          user_id: string;
          type: 'appointment' | 'payment' | 'reminder' | 'system' | 'message';
          title: string;
          message: string;
          link: string | null;
          is_read: boolean;
          created_at: string;
        };
      };
    };
  };
};
